import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { OverlayLoginComponent } from 'src/app/authentication/components/overlay-login/overlay-login.component';
import { OUT_OF_STOCK } from 'src/app/core/constants/error-messages';
import { AddToCart } from 'src/app/core/services/cart/cart-api.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { ConversionTrackingService } from 'src/app/core/services/conversion/conversion-tracking.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PendingCartService } from 'src/app/core/services/pending-cart/pending-cart.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { createDefaultCart } from 'src/app/core/utilities/type-defaults.util';
import { Product } from 'src/app/products/models/product.model';

@Injectable({
  providedIn: 'root'
})
export class ProductDetailService {
  private userService = inject(UserService);
  private overlayService = inject(OverlayService);
  private cartRepositoryService = inject(CartRepositoryService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private platformService = inject(PlatformService);
  private pendingCartService = inject(PendingCartService);
  private conversionTrackingService = inject(ConversionTrackingService);

  // Signal-based state management
  private _errorMessage = signal<string>('');
  readonly errorMessage = this._errorMessage.asReadonly();

  private _currentProductQuantity = signal<number>(1);
  readonly productQuantity = this._currentProductQuantity.asReadonly();

  // Computed signals for derived state
  readonly isBrowser = computed(() => this.platformService.isBrowser());
  readonly isLoginCompleted = computed(() => this._loginCompleted());

  // Login flow
  loginRef!: MyOverlayRef<OverlayLoginComponent>;

  private _loginCompleted = signal<boolean>(false);
  readonly loginCompleted = this._loginCompleted.asReadonly();

  constructor() {
    // No need to set isBrowser in constructor as it's now a computed signal
  }

  updateProductQuantity(quantity: number) {
    this._currentProductQuantity.set(quantity);
  }

  setErrorMessage(message: string) {
    this._errorMessage.set(message);
  }

  clearErrorMessage() {
    this._errorMessage.set('');
  }

  addToCart(cartDetail: { product: Product, bid_price: number, productDetailOverlay: boolean, stayOnCurrentPage?: boolean }): boolean {
    const user = this.userService.appUser.access_token;
    if (!user) {// Store product information for auto-add after login
      this.pendingCartService.storePendingProduct({
        id: cartDetail.product.id,
        bid_price: cartDetail.bid_price,
        quantity: this.productQuantity(),
        productDetailOverlay: cartDetail.productDetailOverlay,
        stayOnCurrentPage: cartDetail.stayOnCurrentPage ?? false
      });

      // Navigate to auth with dynamic return URL
      const currentUrl = this.router.url;
      const returnUrl = cartDetail.stayOnCurrentPage === false ? '/cart' : currentUrl; this.router.navigate(['/auth/login-otp'], {
        queryParams: { returnUrl }
      });

      this.notificationService.showInfo('Please log in to your account to continue.');
      return true; // Indicates login redirect happened
    } else {
      this._addToCart(cartDetail.product.id, cartDetail.bid_price, this.productQuantity(), cartDetail.stayOnCurrentPage ?? true);
      return false; // Indicates no login redirect
    }
  }

  async _addToCart(id: string, bid_price = 0, quantity = 0, stayOnCurrentPage = false) {
    const cart: AddToCart = {
      cart_items: [
        {
          product: id,
          quantity: quantity ? quantity : this.productQuantity(),
          ...(bid_price && bid_price > 0 && {
            bid_price
          }),
        }
      ]
    };

    try {
      await this.cartRepositoryService.addProductToCart(cart);
      this.conversionTrackingService.trackAddToCartConversion(createDefaultCart());

      if (!stayOnCurrentPage) {
        this.router.navigateByUrl('/cart');
      }

      // Showing success notification for regular cart add
      this.notificationService.showSuccess('Product added to cart successfully.');
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error?.error?.code === '1103') {
          this.setErrorMessage('Out of Stock');
        }
      }

      if (error instanceof HttpErrorResponse) {
        this.notificationService.showWarning(`${OUT_OF_STOCK}`);
      }
    }
  }

  addToCart$(cartDetail: { product: Product, bid_price: number, productDetailOverlay: boolean, stayOnCurrentPage?: boolean }) {
    const user = this.userService.appUser;
    if (!user?.access_token) {
      // Store product information for auto-add after login
      this.pendingCartService.storePendingProduct({
        id: cartDetail.product.id,
        bid_price: cartDetail.bid_price,
        quantity: this.productQuantity(),
        productDetailOverlay: cartDetail.productDetailOverlay,
        stayOnCurrentPage: cartDetail.stayOnCurrentPage ?? false
      });

      // Navigate to auth with dynamic return URL
      const currentUrl = this.router.url;
      const returnUrl = cartDetail.stayOnCurrentPage === false ? '/cart' : currentUrl;

      this.router.navigate(['/auth/login-otp'], {
        queryParams: { returnUrl }
      });

      this.notificationService.showInfo('Please log in to your account to continue.');
      return of(null);
    } else {
      return this._addToCart$(cartDetail.product.id, cartDetail.bid_price, this.productQuantity(), cartDetail.stayOnCurrentPage ?? true);
    }
  }

  _addToCart$(id: string, bid_price = 0, quantity = 0, stayOnCurrentPage = false) {
    this.cartRepositoryService.setPageMasking(true);
    const cart: AddToCart = {
      cart_items: [
        {
          product: id,
          quantity: quantity ? quantity : this.productQuantity(),
          ...(bid_price && bid_price > 0 && {
            bid_price
          }),
        }
      ]
    };

    return of(null).pipe(
      tap(() => {
        // Execute the Promise-based operation
        this.cartRepositoryService.addProductToCart(cart).then(() => {
          this.cartRepositoryService.setPageMasking(false);
          this.conversionTrackingService.trackAddToCartConversion(createDefaultCart());

          if (!stayOnCurrentPage) {
            this.router.navigateByUrl('/cart');
          }

          this.notificationService.showSuccess('Product added to cart successfully.');
        }).catch((error: any) => {
          this.cartRepositoryService.setButtonLoading(false);
          this.cartRepositoryService.setPageMasking(false);
          if (error instanceof HttpErrorResponse) {
            if (error?.error?.code === '1103') {
              this.setErrorMessage('Out of Stock');
            }
          }

          if (error instanceof HttpErrorResponse) {
            this.notificationService.showWarning(`${OUT_OF_STOCK}`);
          }
        });
      })
    );
  }

  login() {
    this.loginRef = this.overlayService.open(OverlayLoginComponent, null, true, 'centered', 'login-overlay-container', 'block-scroll');
    this.loginRef.afterClosed$.pipe(
      filter(data => !!data?.data?.success),
      take(1)
    ).subscribe(() => {
      this._loginCompleted.set(true);
    });
  }
}
